import { keys } from "@arti/utilities";
import { useEffect, useState } from "react";

import type {
  TimingEntriesResponse,
  TimingEntry,
  TimingEntryOrder,
  TimingFilters,
  TimingSummaryResponse,
  TimingSummaryTotals,
} from "../types/timings";

import {
  getTimingSummaryAsUser,
  listTimingEntriesAsUser,
} from "../dashboard-client/index";
import { MS_IN_DAY } from "../utils/usageConstants";

const DEFAULT_WINDOW_DAYS = 30;

export interface TimingStatsState {
  filters: Required<Pick<TimingFilters, "fromTimestampMs" | "toTimestampMs">> &
    Omit<TimingFilters, "fromTimestampMs" | "toTimestampMs">;
  order: TimingEntryOrder;
  page: number;
  pageSize: number;
  summary: TimingSummaryResponse | null;
  entries: TimingEntriesResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export interface UseTimingStatsOptions {
  /**
   * Note: options are only read on first render; update the hook key/reset externally if you need to apply changes.
   */
  initialFilters?: TimingFilters;
  debounceMs?: number;
  initialPageSize?: number;
  initialOrder?: TimingEntryOrder;
}

export interface UseTimingStatsResult extends TimingStatsState {
  totals: TimingSummaryTotals | null;
  entriesRows: TimingEntry[];
  totalRows: number;
  pageCount: number;
  setFilters: (patch: TimingFilters) => void;
  setDateRange: (fromTimestampMs: number, toTimestampMs: number) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  refresh: () => void;
  reset: () => void;
}

interface FetchTimingSummaryOptions {
  filters?: TimingFilters;
}

interface FetchTimingEntriesOptions {
  filters?: TimingFilters;
  page?: number;
  pageSize?: number;
  order?: TimingEntryOrder;
}

interface TimingApi {
  fetchTimingSummary: (
    options?: FetchTimingSummaryOptions,
  ) => Promise<TimingSummaryResponse>;
  fetchTimingEntries: (
    options?: FetchTimingEntriesOptions,
  ) => Promise<TimingEntriesResponse>;
}

function buildTimingFiltersQuery(filters: TimingFilters | undefined): {
  from?: string;
  to?: string;
  taskId?: string;
  ticketId?: string;
  streamId?: string;
  runId?: string;
  kind?: string;
  name?: string;
  stepNumber?: string;
  minDurationMs?: string;
  maxDurationMs?: string;
} {
  if (!filters) {
    return {};
  }

  return {
    from:
      typeof filters.fromTimestampMs === "number"
        ? String(filters.fromTimestampMs)
        : undefined,
    kind: filters.kinds?.length ? filters.kinds.join(",") : undefined,
    maxDurationMs:
      typeof filters.maxDurationMs === "number"
        ? String(filters.maxDurationMs)
        : undefined,
    minDurationMs:
      typeof filters.minDurationMs === "number"
        ? String(filters.minDurationMs)
        : undefined,
    name: filters.names?.length ? filters.names.join(",") : undefined,
    runId: filters.runId,
    stepNumber:
      typeof filters.stepNumber === "number"
        ? String(filters.stepNumber)
        : undefined,
    streamId: filters.streamId,
    taskId: filters.taskId,
    ticketId: filters.ticketId,
    to:
      typeof filters.toTimestampMs === "number"
        ? String(filters.toTimestampMs)
        : undefined,
  };
}

async function fetchTimingSummary(
  options: FetchTimingSummaryOptions = {},
): Promise<TimingSummaryResponse> {
  const { data } = await getTimingSummaryAsUser({
    query: buildTimingFiltersQuery(options.filters),
  });
  return data;
}

async function fetchTimingEntries(
  options: FetchTimingEntriesOptions = {},
): Promise<TimingEntriesResponse> {
  const { data } = await listTimingEntriesAsUser({
    query: {
      ...buildTimingFiltersQuery(options.filters),
      order: options.order,
      page: typeof options.page === "number" ? String(options.page) : undefined,
      pageSize:
        typeof options.pageSize === "number"
          ? String(options.pageSize)
          : undefined,
    },
  });
  return data;
}

export function useTimingStats(
  options: UseTimingStatsOptions = {},
): UseTimingStatsResult {
  const [controller] = useState(
    () =>
      new TimingStatsController({
        api: { fetchTimingEntries, fetchTimingSummary },
        debounceMs: options.debounceMs,
        initialFilters: options.initialFilters,
        initialOrder: options.initialOrder,
        initialPageSize: options.initialPageSize,
      }),
  );

  const [state, setState] = useState<TimingStatsState>(() =>
    controller.getSnapshot(),
  );

  useEffect(() => {
    const unsubscribe = controller.subscribe(() =>
      setState(controller.getSnapshot()),
    );
    controller.start();
    return () => {
      unsubscribe();
      controller.dispose();
    };
  }, [controller]);

  const totals = state.summary?.totals ?? null;
  const entriesRows = state.entries?.entries ?? [];
  const totalRows = state.entries?.total ?? 0;
  const pageCount = state.entries
    ? Math.max(1, Math.ceil(state.entries.total / state.entries.pageSize))
    : 1;

  return {
    ...state,
    entriesRows,
    nextPage: controller.nextPage,
    pageCount,
    prevPage: controller.prevPage,
    refresh: controller.refresh,
    reset: controller.reset,
    setDateRange: controller.setDateRange,
    setFilters: controller.setFilters,
    setPage: controller.setPage,
    setPageSize: controller.setPageSize,
    totalRows,
    totals,
  };
}

export class TimingStatsController {
  private readonly api: TimingApi;
  private readonly debounceMs: number;
  private readonly defaultState: TimingStatsState;

  private state: TimingStatsState;
  private listeners = new Set<() => void>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private requestId = 0;
  private hasStarted = false;

  constructor(options: {
    api: TimingApi;
    initialFilters?: TimingFilters;
    debounceMs?: number;
    initialPageSize?: number;
    initialOrder?: TimingEntryOrder;
  }) {
    this.api = options.api;
    this.debounceMs =
      typeof options.debounceMs === "number"
        ? Math.max(0, Math.trunc(options.debounceMs))
        : 250;

    const now = Date.now();
    const defaultRange = buildDefaultDateRange(now);
    const mergedFilters: TimingStatsState["filters"] = {
      fromTimestampMs: defaultRange.fromTimestampMs,
      toTimestampMs: defaultRange.toTimestampMs,
      ...options.initialFilters,
    };

    this.defaultState = {
      entries: null,
      error: null,
      filters: mergedFilters,
      isLoading: false,
      order: options.initialOrder ?? "desc",
      page: 1,
      pageSize:
        typeof options.initialPageSize === "number"
          ? Math.max(1, Math.trunc(options.initialPageSize))
          : 50,
      summary: null,
    };

    this.state = { ...this.defaultState };
  }

  start() {
    if (this.hasStarted) {
      return;
    }
    this.hasStarted = true;
    this.refresh();
  }

  dispose() {
    this.clearDebounce();
    this.listeners.clear();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getSnapshot(): TimingStatsState {
    return this.state;
  }

  reset = () => {
    this.state = {
      ...this.defaultState,
      entries: null,
      error: null,
      isLoading: false,
      summary: null,
    };
    this.emit();
    this.refresh();
  };

  setFilters = (patch: TimingFilters) => {
    const nextFilters = applyFilterPatch(this.state.filters, patch);
    const filtersChanged = !shallowEqual(this.state.filters, nextFilters);
    if (!filtersChanged) {
      return;
    }

    this.state = { ...this.state, filters: nextFilters, page: 1 };
    this.emit();
    this.refreshDebounced();
  };

  setDateRange = (fromTimestampMs: number, toTimestampMs: number) => {
    this.setFilters({ fromTimestampMs, toTimestampMs });
  };

  setPage = (page: number) => {
    const next = Math.max(1, Math.trunc(page));
    if (next === this.state.page) {
      return;
    }
    this.state = { ...this.state, page: next };
    this.emit();
    this.refresh({ immediate: true });
  };

  setPageSize = (pageSize: number) => {
    const next = Math.max(1, Math.trunc(pageSize));
    if (next === this.state.pageSize) {
      return;
    }
    this.state = { ...this.state, page: 1, pageSize: next };
    this.emit();
    this.refresh({ immediate: true });
  };

  nextPage = () => {
    const total = this.state.entries?.total ?? 0;
    const pageSize = this.state.entries?.pageSize ?? this.state.pageSize;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    this.setPage(Math.min(pageCount, this.state.page + 1));
  };

  prevPage = () => {
    this.setPage(Math.max(1, this.state.page - 1));
  };

  refresh = (options: { immediate?: boolean } = {}) => {
    if (options.immediate) {
      this.clearDebounce();
      void this.fetchNow();
      return;
    }
    this.refreshDebounced();
  };

  private refreshDebounced() {
    this.clearDebounce();
    if (this.debounceMs === 0) {
      void this.fetchNow();
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.fetchNow();
    }, this.debounceMs);
  }

  private clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private emit() {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private async fetchNow(): Promise<void> {
    const currentRequestId = (this.requestId += 1);
    const snapshot = this.state;

    this.state = { ...snapshot, error: null, isLoading: true };
    this.emit();

    try {
      const { filters } = snapshot;
      const [summary, entries] = await Promise.all([
        this.api.fetchTimingSummary({ filters }),
        this.api.fetchTimingEntries({
          filters,
          order: snapshot.order,
          page: snapshot.page,
          pageSize: snapshot.pageSize,
        }),
      ]);

      if (currentRequestId !== this.requestId) {
        return;
      }

      this.state = {
        ...this.state,
        entries,
        error: null,
        isLoading: false,
        summary,
      };
      this.emit();
    } catch (error) {
      if (currentRequestId !== this.requestId) {
        return;
      }

      this.state = {
        ...this.state,
        error: error instanceof Error ? error : new Error("Unknown error"),
        isLoading: false,
      };
      this.emit();
    }
  }
}

function buildDefaultDateRange(nowMs: number): {
  fromTimestampMs: number;
  toTimestampMs: number;
} {
  const toTimestampMs = Math.max(0, Math.trunc(nowMs));
  const fromTimestampMs = Math.max(
    0,
    toTimestampMs - DEFAULT_WINDOW_DAYS * MS_IN_DAY,
  );
  return { fromTimestampMs, toTimestampMs };
}

function applyFilterPatch(
  current: TimingStatsState["filters"],
  patch: TimingFilters,
): TimingStatsState["filters"] {
  const merged = { ...current, ...patch };

  return {
    fromTimestampMs: merged.fromTimestampMs,
    toTimestampMs: merged.toTimestampMs,
    ...(merged.kinds === undefined ? {} : { kinds: merged.kinds }),
    ...(merged.maxDurationMs === undefined
      ? {}
      : { maxDurationMs: merged.maxDurationMs }),
    ...(merged.minDurationMs === undefined
      ? {}
      : { minDurationMs: merged.minDurationMs }),
    ...(merged.names === undefined ? {} : { names: merged.names }),
    ...(merged.runId === undefined ? {} : { runId: merged.runId }),
    ...(merged.stepNumber === undefined
      ? {}
      : { stepNumber: merged.stepNumber }),
    ...(merged.streamId === undefined ? {} : { streamId: merged.streamId }),
    ...(merged.taskId === undefined ? {} : { taskId: merged.taskId }),
    ...(merged.ticketId === undefined ? {} : { ticketId: merged.ticketId }),
  };
}

function shallowEqual<T extends Record<string, unknown>>(a: T, b: T): boolean {
  const aKeys = keys(a);
  const bKeys = keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  for (const key of aKeys) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}

export const __testing = {
  DEFAULT_WINDOW_DAYS,
  MS_IN_DAY,
  TimingStatsController,
  buildDefaultDateRange,
};
